import { z } from 'zod';
import type { Recipe } from './recipe.js';
import { convertQty } from './uomConvert.js';

export const CateringEventStatusSchema = z.enum([
  'draft', 'quoted', 'confirmed', 'in_production', 'completed', 'cancelled',
]);
export type CateringEventStatus = z.infer<typeof CateringEventStatusSchema>;

export const CateringLogisticsModeSchema = z.enum([
  'onsite', 'internal_transfer', 'external_delivery',
]);
export type CateringLogisticsMode = z.infer<typeof CateringLogisticsModeSchema>;

export const CateringMenuLineSchema = z.object({
  recipe_id: z.string().uuid(),
  recipe_name: z.string().min(1).max(200),
  portions_per_guest: z.number().positive().default(1),
});
export type CateringMenuLine = z.infer<typeof CateringMenuLineSchema>;

export const CateringQuoteRevisionSchema = z.object({
  id: z.string().uuid(),
  revision: z.number().int().positive(),
  created_at: z.string().datetime(),
  event_name: z.string().min(1).max(200),
  customer_name: z.string().max(200).nullable(),
  event_date: z.string().date(),
  season_start: z.string().date(),
  season_end: z.string().date(),
  as_of: z.string().date(),
  guests_min: z.number().int().positive(),
  guests_expected: z.number().int().positive(),
  guests_max: z.number().int().positive(),
  selected_recipe_ids: z.array(z.string().uuid()).max(250),
  logistics_mode: CateringLogisticsModeSchema,
  destination_id: z.string().uuid().nullable(),
  input_snapshot: z.record(z.unknown()),
  result_snapshot: z.record(z.unknown()),
});
export type CateringQuoteRevision = z.infer<typeof CateringQuoteRevisionSchema>;

export const CateringDischargeLineSchema = z.object({
  food_id: z.string().uuid(),
  food_name: z.string().min(1).max(200),
  recipe_ids: z.array(z.string().uuid()).min(1).max(250),
  required_qty: z.number().nonnegative(),
  allocated_qty: z.number().positive(),
  leftover_qty: z.number().nonnegative(),
  consumed_qty: z.number().nonnegative(),
  uom: z.string().min(1).max(30),
  batch_lot: z.string().max(200).nullable(),
  expiration_date: z.string().date().nullable(),
  supplier_id: z.string().uuid().nullable(),
  document_id: z.string().uuid().nullable(),
  storage_unit_id: z.string().uuid().nullable(),
}).superRefine((line, ctx) => {
  if (line.leftover_qty > line.allocated_qty) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['leftover_qty'], message: 'leftover exceeds allocation' });
  }
  if (Math.abs(line.consumed_qty - (line.allocated_qty - line.leftover_qty)) > 0.000_001) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['consumed_qty'], message: 'consumed quantity mismatch' });
  }
});
export type CateringDischargeLine = z.infer<typeof CateringDischargeLineSchema>;

export const CateringDischargeDraftSchema = z.object({
  version: z.literal(1),
  prepared_at: z.string().datetime(),
  prepared_by: z.string().uuid().nullable(),
  lines: z.array(CateringDischargeLineSchema).max(2_000),
}).nullable().default(null);
export type CateringDischargeDraft = z.infer<typeof CateringDischargeDraftSchema>;

export const CateringDischargeSnapshotSchema = z.object({
  version: z.literal(1),
  confirmed_at: z.string().datetime(),
  confirmed_by: z.string().uuid().nullable(),
  command_id: z.string().uuid(),
  lines: z.array(CateringDischargeLineSchema).min(1).max(2_000),
});
export type CateringDischargeSnapshot = z.infer<typeof CateringDischargeSnapshotSchema>;

export const CateringEventSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid(),
  name: z.string().min(1).max(200),
  customer_name: z.string().max(200).nullable(),
  event_date: z.string().date(),
  status: CateringEventStatusSchema,
  guests_expected: z.number().int().positive(),
  logistics_mode: CateringLogisticsModeSchema,
  destination_id: z.string().uuid().nullable(),
  menu_lines: z.array(CateringMenuLineSchema).max(250),
  quote_revisions: z.array(CateringQuoteRevisionSchema).max(100),
  source_document_ids: z.array(z.string().uuid()).max(500).default([]),
  shipment_id: z.string().uuid().nullable(),
  discharge_draft: CateringDischargeDraftSchema,
  discharge_snapshot: CateringDischargeSnapshotSchema.nullable().default(null),
  notes: z.string().max(5000).nullable(),
  version: z.number().int().nonnegative(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
});
export type CateringEvent = z.infer<typeof CateringEventSchema>;

export interface CateringAvailableStock {
  foodId: string;
  qty: number;
  uom: string;
}

export interface CateringProcurementMissing {
  eventId: string;
  recipeId: string;
  reason:
    | 'menu_missing'
    | 'recipe_not_found'
    | 'recipe_yield_missing'
    | 'ingredient_qty_missing'
    | 'ingredient_uom_missing'
    | 'sub_recipe_link_missing'
    | 'sub_recipe_yield_missing'
    | 'sub_recipe_uom_mismatch'
    | 'sub_recipe_cycle'
    | 'stock_uom_mismatch';
  at: string;
}

export interface CateringProcurementLine {
  foodId: string;
  uom: string;
  requiredQty: number;
  availableQty: number;
  suggestedOrderQty: number;
  eventIds: string[];
}

export interface CateringProcurementResult {
  weekStart: string;
  weekEnd: string;
  includedEventIds: string[];
  lines: CateringProcurementLine[];
  missing: CateringProcurementMissing[];
}

type Requirement = { foodId: string; qty: number; uom: string; eventId: string; sourceRecipeId: string };

export interface CateringEventRequirement {
  foodId: string;
  qty: number;
  uom: string;
  recipeIds: string[];
}

export interface CateringLotCandidate {
  foodId: string;
  batchLot: string | null;
  expirationDate: string | null;
  availableQty: number;
  uom: string;
  supplierId: string | null;
  documentId: string | null;
  storageUnitId: string | null;
}

export interface CateringLotAllocation extends CateringLotCandidate {
  requiredQty: number;
  allocatedQty: number;
  recipeIds: string[];
}

export interface CateringDocumentCandidate {
  id: string;
  documentDate: string | null;
}

export interface CateringDocumentSuggestion {
  documentId: string;
  matchingFoodIds: string[];
  suggested: boolean;
}

const round6 = (value: number): number => Math.round(value * 1_000_000) / 1_000_000;

function addMissing(
  missing: CateringProcurementMissing[],
  eventId: string,
  recipeId: string,
  reason: CateringProcurementMissing['reason'],
  at: string,
): void {
  missing.push({ eventId, recipeId, reason, at });
}

function expandRecipe(input: {
  eventId: string;
  recipe: Recipe;
  batchFactor: number;
  recipesById: ReadonlyMap<string, Recipe>;
  path: ReadonlySet<string>;
  sourceRecipeId: string;
  requirements: Requirement[];
  missing: CateringProcurementMissing[];
}): void {
  const { eventId, recipe, batchFactor, recipesById, requirements, missing } = input;
  if (input.path.has(recipe.id) || input.path.size > 8) {
    addMissing(missing, eventId, recipe.id, 'sub_recipe_cycle', recipe.id);
    return;
  }
  const path = new Set(input.path).add(recipe.id);
  for (const ingredient of recipe.ingredients ?? []) {
    if (ingredient.qty == null || !Number.isFinite(ingredient.qty) || ingredient.qty <= 0) {
      addMissing(missing, eventId, recipe.id, 'ingredient_qty_missing', ingredient.food_id);
      continue;
    }
    if (!ingredient.uom?.trim()) {
      addMissing(missing, eventId, recipe.id, 'ingredient_uom_missing', ingredient.food_id);
      continue;
    }
    requirements.push({
      foodId: ingredient.food_id,
      qty: round6(ingredient.qty * batchFactor),
      uom: ingredient.uom,
      eventId,
      sourceRecipeId: input.sourceRecipeId,
    });
  }

  const links = new Map((recipe.parent_recipes ?? []).map((link) => [link.recipe_id, link]));
  for (const subId of recipe.parent_recipe_ids ?? []) {
    const sub = recipesById.get(subId);
    if (!sub) {
      addMissing(missing, eventId, recipe.id, 'recipe_not_found', subId);
      continue;
    }
    const link = links.get(subId);
    if (!link || link.qty == null || !link.uom) {
      addMissing(missing, eventId, recipe.id, 'sub_recipe_link_missing', subId);
      continue;
    }
    if (sub.yield_qty == null || sub.yield_qty <= 0 || !sub.yield_uom) {
      addMissing(missing, eventId, recipe.id, 'sub_recipe_yield_missing', subId);
      continue;
    }
    const usedInSubUom = convertQty(link.qty * batchFactor, link.uom, sub.yield_uom);
    if (usedInSubUom == null) {
      addMissing(missing, eventId, recipe.id, 'sub_recipe_uom_mismatch', subId);
      continue;
    }
    expandRecipe({
      eventId,
      recipe: sub,
      batchFactor: usedInSubUom / sub.yield_qty,
      recipesById,
      path,
      sourceRecipeId: input.sourceRecipeId,
      requirements,
      missing,
    });
  }
}

/** Calcola il fabbisogno di un singolo evento senza leggere o mutare il magazzino. */
export function computeCateringEventRequirements(input: {
  event: CateringEvent;
  recipes: readonly Recipe[];
  foodUomById?: Readonly<Record<string, string>>;
}): { lines: CateringEventRequirement[]; missing: CateringProcurementMissing[] } {
  const recipesById = new Map(input.recipes.map((recipe) => [recipe.id, recipe]));
  const requirements: Requirement[] = [];
  const missing: CateringProcurementMissing[] = [];
  if (input.event.menu_lines.length === 0) {
    addMissing(missing, input.event.id, '', 'menu_missing', input.event.id);
  }
  for (const menuLine of input.event.menu_lines) {
    const recipe = recipesById.get(menuLine.recipe_id);
    if (!recipe) {
      addMissing(missing, input.event.id, menuLine.recipe_id, 'recipe_not_found', menuLine.recipe_id);
      continue;
    }
    if (!recipe.yield_portions || recipe.yield_portions <= 0) {
      addMissing(missing, input.event.id, recipe.id, 'recipe_yield_missing', recipe.id);
      continue;
    }
    expandRecipe({
      eventId: input.event.id,
      recipe,
      batchFactor: (input.event.guests_expected * menuLine.portions_per_guest) / recipe.yield_portions,
      recipesById,
      path: new Set(),
      sourceRecipeId: menuLine.recipe_id,
      requirements,
      missing,
    });
  }
  const byFood = new Map<string, CateringEventRequirement>();
  for (const requirement of requirements) {
    const targetUom = input.foodUomById?.[requirement.foodId] ?? requirement.uom;
    const converted = convertQty(requirement.qty, requirement.uom, targetUom);
    if (converted == null) {
      addMissing(missing, input.event.id, '', 'stock_uom_mismatch', requirement.foodId);
      continue;
    }
    const current = byFood.get(requirement.foodId) ?? { foodId: requirement.foodId, qty: 0, uom: targetUom, recipeIds: [] };
    current.qty = round6(current.qty + converted);
    if (!current.recipeIds.includes(requirement.sourceRecipeId)) current.recipeIds.push(requirement.sourceRecipeId);
    byFood.set(requirement.foodId, current);
  }
  return { lines: [...byFood.values()].sort((a, b) => a.foodId.localeCompare(b.foodId)), missing };
}

/**
 * Propone uno split FEFO. I lotti appartenenti ai documenti scelti vengono
 * consumati prima; gli altri restano disponibili per coprire eventuali buchi.
 */
export function allocateCateringLots(input: {
  requirements: readonly CateringEventRequirement[];
  lots: readonly CateringLotCandidate[];
  selectedDocumentIds?: ReadonlySet<string>;
}): { allocations: CateringLotAllocation[]; shortages: CateringEventRequirement[] } {
  const allocations: CateringLotAllocation[] = [];
  const shortages: CateringEventRequirement[] = [];
  for (const requirement of input.requirements) {
    let remaining = requirement.qty;
    const candidates = input.lots
      .filter((lot) => lot.foodId === requirement.foodId && lot.availableQty > 0)
      .map((lot) => ({ lot, available: convertQty(lot.availableQty, lot.uom, requirement.uom) }))
      .filter((entry): entry is { lot: CateringLotCandidate; available: number } => entry.available != null && entry.available > 0)
      .sort((left, right) => {
        const leftSelected = left.lot.documentId != null && input.selectedDocumentIds?.has(left.lot.documentId) === true;
        const rightSelected = right.lot.documentId != null && input.selectedDocumentIds?.has(right.lot.documentId) === true;
        if (leftSelected !== rightSelected) return leftSelected ? -1 : 1;
        return (left.lot.expirationDate ?? '9999-12-31').localeCompare(right.lot.expirationDate ?? '9999-12-31');
      });
    for (const { lot, available } of candidates) {
      if (remaining <= 0.000_001) break;
      const allocatedQty = round6(Math.min(remaining, available));
      allocations.push({ ...lot, requiredQty: requirement.qty, allocatedQty, uom: requirement.uom, recipeIds: [...requirement.recipeIds] });
      remaining = round6(remaining - allocatedQty);
    }
    if (remaining > 0.000_001) shortages.push({ ...requirement, qty: remaining });
  }
  return { allocations, shortages };
}

/** DDT utili nei sette giorni precedenti, ordinati per copertura e vicinanza. */
export function suggestCateringDocuments(input: {
  eventDate: string;
  requirements: readonly CateringEventRequirement[];
  lots: readonly CateringLotCandidate[];
  documents: readonly CateringDocumentCandidate[];
}): CateringDocumentSuggestion[] {
  const requiredFoodIds = new Set(input.requirements.map((line) => line.foodId));
  const start = new Date(`${input.eventDate}T00:00:00.000Z`);
  start.setUTCDate(start.getUTCDate() - 7);
  const windowStart = start.toISOString().slice(0, 10);
  return input.documents.map((document) => {
    const matchingFoodIds = [...new Set(input.lots
      .filter((lot) => lot.documentId === document.id && lot.availableQty > 0 && requiredFoodIds.has(lot.foodId))
      .map((lot) => lot.foodId))].sort();
    return {
      documentId: document.id,
      matchingFoodIds,
      suggested: matchingFoodIds.length > 0
        && document.documentDate != null
        && document.documentDate >= windowStart
        && document.documentDate <= input.eventDate,
    };
  }).filter((candidate) => candidate.matchingFoodIds.length > 0)
    .sort((left, right) => Number(right.suggested) - Number(left.suggested)
      || right.matchingFoodIds.length - left.matchingFoodIds.length
      || left.documentId.localeCompare(right.documentId));
}

/**
 * Aggrega il fabbisogno della settimana dagli eventi confermati. Le giacenze
 * sono soltanto una copertura del fabbisogno: questa funzione non crea ordini,
 * riserve o movimenti di magazzino.
 */
export function computeCateringProcurement(input: {
  weekStart: string;
  events: readonly CateringEvent[];
  recipes: readonly Recipe[];
  availableStock: readonly CateringAvailableStock[];
  foodUomById?: Readonly<Record<string, string>>;
}): CateringProcurementResult {
  const start = new Date(`${input.weekStart}T00:00:00.000Z`);
  if (Number.isNaN(start.valueOf())) throw new RangeError('weekStart must be an ISO date');
  const end = new Date(start);
  end.setUTCDate(end.getUTCDate() + 6);
  const weekEnd = end.toISOString().slice(0, 10);
  const recipesById = new Map(input.recipes.map((recipe) => [recipe.id, recipe]));
  const events = input.events.filter((event) =>
    ['confirmed', 'in_production'].includes(event.status)
    && !event.archived_at
    && event.event_date >= input.weekStart
    && event.event_date <= weekEnd
  );
  const requirements: Requirement[] = [];
  const missing: CateringProcurementMissing[] = [];

  for (const event of events) {
    if (event.menu_lines.length === 0) {
      addMissing(missing, event.id, '', 'menu_missing', event.id);
      continue;
    }
    for (const menuLine of event.menu_lines) {
      const recipe = recipesById.get(menuLine.recipe_id);
      if (!recipe) {
        addMissing(missing, event.id, menuLine.recipe_id, 'recipe_not_found', menuLine.recipe_id);
        continue;
      }
      if (!recipe.yield_portions || recipe.yield_portions <= 0) {
        addMissing(missing, event.id, recipe.id, 'recipe_yield_missing', recipe.id);
        continue;
      }
      const portions = event.guests_expected * menuLine.portions_per_guest;
      expandRecipe({
        eventId: event.id,
        recipe,
        batchFactor: portions / recipe.yield_portions,
        recipesById,
        path: new Set(),
        sourceRecipeId: menuLine.recipe_id,
        requirements,
        missing,
      });
    }
  }

  const byFood = new Map<string, CateringProcurementLine>();
  for (const requirement of requirements) {
    const targetUom = input.foodUomById?.[requirement.foodId] ?? requirement.uom;
    const qty = convertQty(requirement.qty, requirement.uom, targetUom);
    if (qty == null) {
      addMissing(missing, requirement.eventId, '', 'stock_uom_mismatch', requirement.foodId);
      continue;
    }
    const current = byFood.get(requirement.foodId) ?? {
      foodId: requirement.foodId,
      uom: targetUom,
      requiredQty: 0,
      availableQty: 0,
      suggestedOrderQty: 0,
      eventIds: [],
    };
    current.requiredQty = round6(current.requiredQty + qty);
    if (!current.eventIds.includes(requirement.eventId)) current.eventIds.push(requirement.eventId);
    byFood.set(requirement.foodId, current);
  }

  for (const stock of input.availableStock) {
    const line = byFood.get(stock.foodId);
    if (!line || stock.qty <= 0) continue;
    const qty = convertQty(stock.qty, stock.uom, line.uom);
    if (qty == null) {
      addMissing(missing, line.eventIds[0] ?? '', '', 'stock_uom_mismatch', stock.foodId);
      continue;
    }
    line.availableQty = round6(line.availableQty + qty);
  }
  for (const line of byFood.values()) {
    line.suggestedOrderQty = round6(Math.max(0, line.requiredQty - line.availableQty));
    line.eventIds.sort();
  }
  return {
    weekStart: input.weekStart,
    weekEnd,
    includedEventIds: events.map((event) => event.id).sort(),
    lines: [...byFood.values()].sort((a, b) => b.suggestedOrderQty - a.suggestedOrderQty || a.foodId.localeCompare(b.foodId)),
    missing,
  };
}
